/**
 * components/Settings/AI/APIKeyEntryStep.tsx
 * API key entry step for the AI setup wizard
 *
 * Second step of the wizard — provides a masked input field for the API key
 * with toggle visibility, provider-specific placeholder text and help link,
 * and basic client-side format validation (prefix check).
 *
 * Work Unit: WU01 - AI Setup Wizard
 * Task: T03 - API Key Entry Step
 * Sprint: S02 - AI Setup & Settings UI
 * Project: P09 - AI Integration
 */

// 1. External dependencies
import React, { useState } from 'react';
import { ExternalLink, Eye, EyeOff } from 'lucide-react';

// 2. Workspace packages
import { Button, Input } from '@enfomentis/ui';

// 3. Internal absolute imports
import type { AIProviderType } from '@/types/shared/AI';
import { AI_PROVIDER_DISPLAY_MAP } from '@/constants/ai';

// ─── Constants ──────────────────────────────────────────────────────────────

/** Placeholder text per provider */
const PLACEHOLDER_MAP: Readonly<Record<AIProviderType, string>> = {
  anthropic: 'sk-ant-...',
  openai: 'sk-...',
};

/** Help URL for obtaining an API key per provider */
const HELP_URL_MAP: Readonly<Record<AIProviderType, string>> = {
  anthropic: 'https://console.anthropic.com/settings/keys',
  openai: 'https://platform.openai.com/api-keys',
};

/** Expected key prefix per provider for format validation */
const PREFIX_MAP: Readonly<Record<AIProviderType, string>> = {
  anthropic: 'sk-ant-',
  openai: 'sk-',
};

// ─── Props ──────────────────────────────────────────────────────────────────

/**
 * Props for the API Key Entry Step
 *
 * @see APIKeyEntryStep
 */
export interface APIKeyEntryStepProps {
  /** The provider selected in step 1 */
  selectedProvider: AIProviderType;

  /** Current API key value (controlled by parent) */
  apiKey: string;

  /** Called when the user types in the input */
  onApiKeyChange: (key: string) => void;
}

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * API Key Entry Step
 *
 * Masked input with toggle visibility, provider-specific placeholder text
 * and help link, and basic format validation (prefix check).
 *
 * - Input is controlled by parent via `apiKey` / `onApiKeyChange`
 * - Format validation is non-blocking (warning only, does not prevent Next)
 * - Help link opens the provider's API key page in a new tab
 *
 * @example
 * ```tsx
 * <APIKeyEntryStep
 *   selectedProvider="openai"
 *   apiKey={apiKey}
 *   onApiKeyChange={setApiKey}
 * />
 * ```
 */
export const APIKeyEntryStep: React.FC<APIKeyEntryStepProps> = ({
  selectedProvider,
  apiKey,
  onApiKeyChange,
}) => {
  // ─── State ────────────────────────────────────────────────────────────

  const [isVisible, setIsVisible] = useState(false);

  // ─── Derived ──────────────────────────────────────────────────────────

  const displayName = AI_PROVIDER_DISPLAY_MAP[selectedProvider];
  const placeholder = PLACEHOLDER_MAP[selectedProvider];
  const helpUrl = HELP_URL_MAP[selectedProvider];
  const expectedPrefix = PREFIX_MAP[selectedProvider];

  const hasFormatError =
    apiKey.length > 0 && !apiKey.startsWith(expectedPrefix);

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">
        Enter Your {displayName} API Key
      </h3>

      <Input
        label="API Key"
        type={isVisible ? 'text' : 'password'}
        placeholder={placeholder}
        value={apiKey}
        onValueChange={onApiKeyChange}
        isInvalid={hasFormatError}
        errorMessage={
          hasFormatError
            ? `Key should start with ${expectedPrefix}`
            : undefined
        }
        endContent={
          <Button
            isIconOnly
            variant="light"
            size="sm"
            aria-label={isVisible ? 'Hide API key' : 'Show API key'}
            onPress={() => setIsVisible((prev) => !prev)}
            className="min-w-unit-8"
          >
            {isVisible ? (
              <EyeOff className="size-4 text-default-400" />
            ) : (
              <Eye className="size-4 text-default-400" />
            )}
          </Button>
        }
      />

      <div className="flex items-center gap-1.5">
        <ExternalLink className="size-3.5 text-default-400" />
        <a
          href={helpUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-primary hover:underline"
        >
          Get your API key from {displayName}
        </a>
      </div>
    </div>
  );
};
